import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { ChatHeader } from './ChatHeader';
import { ChatMessageItem } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ChatRulesModal } from './ChatRulesModal';

interface ChatPanelProps {
  onClose: () => void;
  className?: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ onClose, className = '' }) => {
  const { messages, hasAcceptedRules, acceptRules, flagMessage, unflagMessage } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showRules, setShowRules] = useState(false);
  const [showFirstTimeRules, setShowFirstTimeRules] = useState(!hasAcceptedRules);
  const [isAutoScroll, setIsAutoScroll] = useState(true);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAutoScroll]);

  // Detect if user has scrolled up — avoids unnecessary setState calls
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const atBottom = scrollHeight - scrollTop - clientHeight < 60;
    // Only update state if the value actually changed
    setIsAutoScroll(prev => (prev === atBottom ? prev : atBottom));
  }, []);

  // Stable callback references for chat message items (avoids re-renders)
  const handleFlag = useCallback((id: number) => flagMessage(id), [flagMessage]);
  const handleUnflag = useCallback((id: number) => unflagMessage(id), [unflagMessage]);

  return (
    <div className={`flex flex-col bg-card border-l border-border h-full relative ${className}`}>
      <ChatHeader onClose={onClose} onShowRules={() => setShowRules(true)} />

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0"
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-muted-foreground">No messages yet. Say hello!</p>
          </div>
        ) : (
          <div className="py-1">
            {messages.map((msg) => (
              <ChatMessageItem key={msg.id} message={msg} onFlag={handleFlag} onUnflag={handleUnflag} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {!isAutoScroll && messages.length > 0 && (
        <button
          onClick={() => {
            setIsAutoScroll(true);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="absolute bottom-14 lg:bottom-14 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[11px] font-medium px-3 py-1 rounded-full shadow-lg hover:opacity-90 transition-opacity"
        >
          New messages
        </button>
      )}

      <ChatInput />

      {/* Mobile: extra padding so input clears the elevated center logo on the bottom nav */}
      <div className="lg:hidden h-4 flex-shrink-0 bg-card" />

      {/* Rules modals */}
      <ChatRulesModal
        isOpen={showFirstTimeRules}
        onClose={() => setShowFirstTimeRules(false)}
        onAccept={acceptRules}
        showAccept={true}
      />
      <ChatRulesModal
        isOpen={showRules}
        onClose={() => setShowRules(false)}
      />
    </div>
  );
};
